import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { Col, Row } from "react-bootstrap";
import {
  AMC_QUERY_EXPLORER_BREADCRUMB_SPEC,
  CANNED_QUERIES,
  FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT,
  FEATURED_REPORTS,
} from "./utils";
import { DASHBOARD_LINKS } from "utils/dashboardLinks";
import { useMemo } from "react";
import { Redirect, useParams } from "react-router-dom";
import moment from "moment";
import Loading from "components/core/blocks/Loading";
import { useAMCReportData } from "./hooks/useAMCReportData";
import { useAMCQueryData } from "./hooks/useAMCQueryData";
import { useModuleEnabled } from "redux/selectors/modules";
import AMCReportTable from "./AMCReportTable";
import AMCGeoReport from "./AMCGeoReport";
import AMCFirstTouchAttrReport from "./AMCFirstTouchAttrReport";
import AMCSearchTermPlacementReport from "./AMCSearchTermPlacementReport";
import PathToPurchaseReport from "./pathToPurchase/PathToPurchaseReport";
import { formatSubscriptionName } from "./AMCReportsListing";
import AMCReachImpressionFrequency from "./AMCReachImpressionFrequency";
import AMCTimeToConversion from "./AMCTimeToConversion";

const QueryTypeReport = ({ reportData, reportDetails, queryDetails, isLoading }) => {
  switch (queryDetails?.query_type) {
    case "DSP_CAMPAIGN_PERFORMANCE_BY_LOCATION":
      return <AMCGeoReport reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />;
    case "CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION":
      return (
        <AMCFirstTouchAttrReport reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />
      );
    case "CAMPAIGN_STR_PLACEMENT_PERFORMANCE":
      return (
        <AMCSearchTermPlacementReport
          reportData={reportData}
          reportDetails={reportDetails}
          queryDetails={queryDetails}
        />
      );
    case "PATH_TO_PURCHASE_GROUPS_INTERNAL":
      return (
        <PathToPurchaseReport
          reportData={reportData}
          reportDetails={reportDetails}
          queryDetails={queryDetails}
          isLoading={isLoading}
        />
      );
    case "REACH_IMPRESSION_FREQUENCY":
      return (
        <AMCReachImpressionFrequency
          reportData={reportData}
          reportDetails={reportDetails}
          queryDetails={queryDetails}
          isLoading={isLoading}
        />
      );
    case "TIME_TO_CONVERSION":
      return (
        <AMCTimeToConversion
          reportData={reportData}
          reportDetails={reportDetails}
          queryDetails={queryDetails}
          isLoading={isLoading}
        />
      );
    default:
      return <AMCReportTable reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />;
  }
};

const AMCReport = () => {
  const { id: reportId } = useParams();

  const hasAMCModule = useModuleEnabled("AMC");

  const { data: queryData, isLoading } = useAMCQueryData();

  const {
    reportMetadata: reportDetails,
    isLoading: isReportDetailsLoading,
    data: reportData,
  } = useAMCReportData(reportId);

  const { reportMetadata: queryReportsData } = useAMCReportData(null, reportDetails?.query_type, false);

  const queryDetails = useMemo(() => {
    if (FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT.includes(reportDetails?.query_type)) {
      return { ...FEATURED_REPORTS.find((option) => option.name === reportDetails?.query_type), ...reportDetails };
    }

    const querySubscription =
      queryData?.find((subscribedQuery) => {
        return subscribedQuery?.query_type === reportDetails?.query_type;
      }) ?? {};
    const reportQueryDetails = CANNED_QUERIES.find((query) => query.name === querySubscription?.query_type);

    return { ...reportQueryDetails, ...querySubscription };
  }, [queryData, reportDetails]);

  const queryOptions = useMemo(() => {
    const subscribedReportOptions =
      queryData?.map((query) => {
        const displayName = formatSubscriptionName(query);
        return { label: displayName, value: query.id };
      }) ?? [];

    const featuredReportOptions =
      FEATURED_REPORTS?.map((reportType) => {
        if (FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT.includes(reportType.name)) {
          return { label: reportType.display_name, value: reportType.href, featured: true };
        }
      })?.filter((reportTypeOption) => reportTypeOption) ?? [];

    return [...subscribedReportOptions, ...featuredReportOptions];
  }, [queryData]);

  const breadCrumbItems = useMemo(() => {
    const breadCrumbSpec = [
      {
        name: "AMC Dashboard",
        href: "/user/amc/dashboard",
        unsorted: true,
        dropdownLinkOptions: DASHBOARD_LINKS,
        dropdownSubtitle: "Reports",
        activeOption: "AMC Dashboard",
      },
      {
        name: queryDetails?.display_name,
        href: queryDetails?.featured ? queryDetails?.href : `/user/amc/dashboard/reports/${queryDetails?.id}`,
        dropdownLinkOptions: queryOptions
          ?.map((query) => {
            const { label, value, featured } = query;
            return {
              name: label,
              href: featured ? value : `/user/amc/dashboard/reports/${value}`,
            };
          })
          .filter((q) => !!q.name),
        searchable: true,
        searchPlaceholderText: "Search subscribed queries...",
        dropdownSubtitle: "Subscribed Queries",
        activeOption: queryDetails?.display_name,
      },
      {
        name:
          reportDetails?.metadata?.timeWindowStart ?
            `${moment(reportDetails?.metadata?.timeWindowStart).format("MMMM D, YYYY")} - ${moment(reportDetails?.metadata?.timeWindowEnd).format("MMMM D, YYYY")}`
          : "",
        unsorted: true,
        href: `/user/amc/dashboard/reports/details/${reportDetails?.id}`,
        dropdownLinkOptions: queryReportsData?.map((report) => {
          const {
            id,
            metadata: { timeWindowStart: startDate, timeWindowEnd: endDate },
          } = report;

          return {
            name: `${moment(startDate).format("MMMM D, YYYY")} - ${moment(endDate).format("MMMM D, YYYY")}`,
            href: `/user/amc/dashboard/reports/details/${id}`,
          };
        }),
        searchable: true,
        searchPlaceholderText: "Search query reports...",
        dropdownSubtitle: "Query Reports",
      },
    ];

    if (!breadCrumbSpec[0].dropdownLinkOptions?.find((o) => o.name === AMC_QUERY_EXPLORER_BREADCRUMB_SPEC.name)) {
      breadCrumbSpec[0].dropdownLinkOptions.push(AMC_QUERY_EXPLORER_BREADCRUMB_SPEC);
    }
    return breadCrumbSpec;
  }, [reportDetails, queryDetails, queryOptions, queryReportsData]);

  if (!hasAMCModule) {
    return <Redirect to="/user/dashboard" />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} hideDateSelector={true} />
        </Col>
      </Row>
      {isReportDetailsLoading || isLoading ?
        <Loading fullPage={true} />
      : <>
          <Row style={{ padding: "20px 150px" }}>
            <Col xs={12} className="text-center">
              <h1 className="fw-bold fs-2 mb-2">{queryDetails?.display_name} Report</h1>
              <p style={{ fontSize: "14px" }}>
                {moment(reportDetails?.metadata?.timeWindowStart).format("MMMM D, YYYY")} to{" "}
                {moment(reportDetails?.metadata?.timeWindowEnd).format("MMMM D, YYYY")}
              </p>
            </Col>
          </Row>
          <Row className="px-5 w-100 pb-5">
            <Col xs={12} className="px-5">
              <QueryTypeReport
                reportData={reportData}
                reportDetails={reportDetails}
                queryDetails={queryDetails}
                isLoading={isReportDetailsLoading}
              />
            </Col>
          </Row>
        </>
      }
    </div>
  );
};

export default AMCReport;
